import { useState, useCallback, useMemo } from 'react';

export interface UseListSelectionProps<T> {
    /**
     * Array of items to manage selection for.
     * Only required when `selectAll` will be called; otherwise optional.
     */
    items?: T[];
    /** Selection mode: single allows one item, multi allows multiple. */
    selectionType: 'single' | 'multi';
    /**
     * Extracts a unique string value from each item for identification.
     * Only required when `selectAll` will be called; otherwise optional.
     */
    getItemValue?: (item: T) => string;
    /** Controlled single-select value. */
    selectedValue?: string;
    /** Controlled multi-select values. */
    multiSelectedValues?: string[];
    /**
     * Called when selection changes.
     * - Single mode: `string` when an item is selected, `undefined` when deselected/cleared.
     * - Multi mode: `string[]` (may be empty when cleared).
     */
    onSelectionChanged?: (value: string | undefined | string[]) => void;
    /** Default single-select value for uncontrolled mode. */
    defaultSelectedValue?: string;
    /** Default multi-select values for uncontrolled mode. */
    defaultMultiSelectedValues?: string[];
}

export interface UseListSelectionReturn {
    /** Currently selected value (single mode). */
    selectedValue: string | undefined;
    /** Currently selected values (multi mode). */
    multiSelectedValues: string[];
    /** Returns whether a given value is selected. */
    isSelected: (value: string) => boolean;
    /** Toggles selection for a given value. */
    handleSelect: (value: string) => void;
    /** Clears all selections. */
    clearSelection: () => void;
    /** Selects all items (multi mode only). */
    selectAll: () => void;
}

export const useListSelection = <T>({
    items,
    selectionType,
    getItemValue,
    selectedValue: controlledSelectedValue,
    multiSelectedValues: controlledMultiSelectedValues,
    onSelectionChanged,
    defaultSelectedValue,
    defaultMultiSelectedValues
}: UseListSelectionProps<T>): UseListSelectionReturn => {
    const [internalSelectedValue, setInternalSelectedValue] = useState<
        string | undefined
    >(defaultSelectedValue);
    const [internalMultiSelectedValues, setInternalMultiSelectedValues] =
        useState<string[]>(defaultMultiSelectedValues ?? []);

    const isControlled =
        selectionType === 'single'
            ? controlledSelectedValue !== undefined
            : controlledMultiSelectedValues !== undefined;

    const selectedValue = isControlled
        ? controlledSelectedValue
        : internalSelectedValue;

    const multiSelectedValues = useMemo(
        () =>
            isControlled
                ? controlledMultiSelectedValues ?? []
                : internalMultiSelectedValues,
        [
            isControlled,
            controlledMultiSelectedValues,
            internalMultiSelectedValues
        ]
    );

    const selectedSet = useMemo(
        () =>
            selectionType === 'multi'
                ? new Set(multiSelectedValues)
                : new Set(selectedValue !== undefined ? [selectedValue] : []),
        [selectionType, multiSelectedValues, selectedValue]
    );

    const isSelected = useCallback(
        (value: string) => selectedSet.has(value),
        [selectedSet]
    );

    const handleSelect = useCallback(
        (value: string) => {
            if (selectionType === 'single') {
                const newValue = selectedValue === value ? undefined : value;

                if (!isControlled) {
                    setInternalSelectedValue(newValue);
                }
                onSelectionChanged?.(newValue);
            } else {
                const newValues = selectedSet.has(value)
                    ? multiSelectedValues.filter(v => v !== value)
                    : [...multiSelectedValues, value];

                if (!isControlled) {
                    setInternalMultiSelectedValues(newValues);
                }
                onSelectionChanged?.(newValues);
            }
        },
        [
            selectionType,
            selectedValue,
            multiSelectedValues,
            selectedSet,
            isControlled,
            onSelectionChanged
        ]
    );

    const clearSelection = useCallback(() => {
        if (selectionType === 'single') {
            if (!isControlled) {
                setInternalSelectedValue(undefined);
            }
            onSelectionChanged?.(undefined);
        } else {
            if (!isControlled) {
                setInternalMultiSelectedValues([]);
            }
            onSelectionChanged?.([]);
        }
    }, [selectionType, isControlled, onSelectionChanged]);

    const selectAll = useCallback(() => {
        if (selectionType !== 'multi' || !items || !getItemValue) {
            return;
        }

        const allValues = items.map(getItemValue);
        if (!isControlled) {
            setInternalMultiSelectedValues(allValues);
        }
        onSelectionChanged?.(allValues);
    }, [selectionType, items, getItemValue, isControlled, onSelectionChanged]);

    return {
        selectedValue,
        multiSelectedValues,
        isSelected,
        handleSelect,
        clearSelection,
        selectAll
    };
};
